import { NoteAddendum } from '~/lib/constants/AddPatientNoteModal.constants';
import {
  ChakraBox,
  ChakraDivider,
  ChakraFlex,
  ChakraText,
  coalesceNoteContentToHtml,
  EditorV2,
  EquipUITheme,
} from '@equip.health/ui';
import {
  getPreferredFullName,
  toSentenceCaseFromCamelCase,
} from '~/lib/util/patient.util';
import { DateTime } from 'luxon';
import {
  formatDisplayValue,
  getActivityChangeKeys,
} from '~/lib/util/patientActivity.util';
import {
  capitalizeFirstWord,
  getDocumentChangeText,
} from '~/lib/util/notes/notes.util';
import './EditorV2ReadonlyStyles.css';

const { label, labelBold } = EquipUITheme.typography;

const getActivity = (addendum: NoteAddendum) => {
  const changedKeys: string[] = getActivityChangeKeys(
    addendum?.before,
    addendum?.after,
  ).filter((key) => key !== 'appointmentExternalId');

  return changedKeys.map((key) => ({
    newValue: formatDisplayValue(key, addendum.after?.[key]),
    oldValue: formatDisplayValue(key, addendum.before?.[key]),
    title: toSentenceCaseFromCamelCase(key),
  }));
};

type Props = {
  idPrefix: string;
  addendum: NoteAddendum;
  index: number;
  isLast: boolean;
  activityWithChange: { from: string[]; to: string[] } | null;
};

export const PatientNoteAddendumItem = ({
  idPrefix,
  addendum,
  index,
  isLast,
  activityWithChange,
}: Props) => {
  const documentChanges = getDocumentChangeText(
    addendum?.noteDocumentChangeHistory,
  );
  return (
    <ChakraBox id={`${idPrefix}__addendum-modal-${addendum.createdAtDateTime}`}>
      <ChakraText
        {...labelBold}
        id={`${idPrefix}__addendum-title-${addendum.createdAtDateTime}`}
        marginTop="24px"
      >{`Addendum #${index + 1}`}</ChakraText>
      <br />
      {getActivity(addendum)?.map((activity) => (
        <ChakraFlex
          alignItems="center"
          id={`${idPrefix}__addendum-activity-${addendum.createdAtDateTime}`}
          key={`patient-note-addenda-activity-${activity.title}-${addendum.createdAtDateTime}`}
        >
          <ChakraText
            {...labelBold}
            id={`${idPrefix}__addendum-title-${addendum.createdAtDateTime}`}
          >
            {activity.title}
          </ChakraText>
          <ChakraText
            {...label}
            id={`${idPrefix}__activity-sentence-${addendum.createdAtDateTime}`}
            marginTop="2px"
          >
            &nbsp; &nbsp;
            {`${activity.oldValue} to ${activity.newValue}`}
          </ChakraText>
        </ChakraFlex>
      ))}

      {activityWithChange && (
        <ChakraFlex
          alignItems="center"
          id={`${idPrefix}__addendum-activitywith-${addendum.createdAtDateTime}`}
          marginBottom="8px"
        >
          <ChakraText {...labelBold}>Activity with</ChakraText>
          <ChakraText {...label} marginLeft="8px">
            {`
  ${activityWithChange.from.map(capitalizeFirstWord).join(', ') || '--'} 
  to 
  ${activityWithChange.to.map(capitalizeFirstWord).join(', ') || '--'}
`}
          </ChakraText>
        </ChakraFlex>
      )}

      {documentChanges.length > 0 && (
        <>
          {documentChanges.map((change) => {
            const labelText = change.files.length > 1 ? 'Files' : 'File';

            return (
              <ChakraFlex
                key={`${change.action}-${change.files.join('-')}`}
                marginBottom="4px"
              >
                <ChakraText {...labelBold}>{labelText}</ChakraText>
                <ChakraText {...label} marginLeft="8px">
                  {`${change.files.join(', ')} ${change.action}`}
                </ChakraText>
              </ChakraFlex>
            );
          })}
        </>
      )}

      <ChakraBox
        className="editor-v2-readonly"
        id={`${idPrefix}__addendum-content-editor-${addendum.createdAtDateTime}`}
      >
        <EditorV2
          hideBorder
          initialContent={coalesceNoteContentToHtml(addendum.content ?? '')}
          readOnly
        />
      </ChakraBox>
      <ChakraFlex
        id={`${idPrefix}__addendum-signed-by-${addendum.createdAtDateTime}`}
        marginBottom="24px"
        marginTop="16px"
      >
        <ChakraText
          {...labelBold}
          id={`${idPrefix}__signed-label-${addendum.createdAtDateTime}`}
          lineHeight="16.8px"
        >
          Signed
        </ChakraText>
        <ChakraText
          {...label}
          color="neutral.secondary"
          id={`${idPrefix}__by-label--${addendum.createdAtDateTime}`}
          lineHeight="16.8px"
          marginLeft="16px"
        >
          by
        </ChakraText>
        <ChakraText
          {...label}
          color="neutral.secondary"
          id={`${idPrefix}__signed-by-name-${addendum.createdAtDateTime}`}
          lineHeight="16.8px"
          marginLeft="8px"
        >
          {addendum.author
            ? getPreferredFullName(
                null,
                addendum.author.firstName,
                addendum.author.lastName,
              )
            : '--'}
        </ChakraText>
        <ChakraText
          {...label}
          color="neutral.secondary"
          id={`${idPrefix}__signed-by-date-time-${addendum.createdAtDateTime}`}
          lineHeight="16.8px"
          marginLeft="8px"
        >
          {DateTime.fromISO(addendum.createdAtDateTime)
            .toFormat("LL/dd/yyyy 'at' hh:mma")
            .toLowerCase()}
        </ChakraText>
      </ChakraFlex>
      {!isLast && <ChakraDivider />}
    </ChakraBox>
  );
};
